import React, { useEffect, useState } from "react";
import { Key, Users, CheckCircle, XCircle, RefreshCw } from "lucide-react";
import api from "../api";

function StatCard({ icon, label, value, color }) {
  return (
    <div style={{ ...styles.card, borderTop: `3px solid ${color}` }}>
      <div style={{ ...styles.cardIcon, background: color + "22" }}>{icon}</div>
      <div>
        <div style={styles.cardVal}>{value}</div>
        <div style={styles.cardLabel}>{label}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [licenses, setLicenses] = useState({});
  const [sessions, setSessions] = useState({});
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [licRes, sessRes, healthRes] = await Promise.all([
        api.get("/admin/list_licenses"),
        api.get("/admin/active_sessions"),
        api.get("/api/health"),
      ]);
      setLicenses(licRes.data);
      setSessions(sessRes.data);
      setHealth(healthRes.data);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const totalKeys = Object.keys(licenses).length;
  const activeKeys = Object.values(licenses).filter((l) => l.active).length;
  const expiredKeys = Object.values(licenses).filter((l) => {
    if (!l.expire) return false;
    return new Date(l.expire) < new Date();
  }).length;
  const activeSess = Object.keys(sessions).length;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Dashboard</h1>
          <p style={styles.sub}>
            Server:{" "}
            <span style={{ color: health ? "#4ade80" : "#f87171" }}>
              {health ? "● Online" : "● Offline"}
            </span>
            {health && (
              <span style={{ color: "#64748b", marginLeft: 12, fontSize: 12 }}>
                {health.time?.slice(0, 19).replace("T", " ")} UTC
              </span>
            )}
          </p>
        </div>
        <button onClick={load} style={styles.refreshBtn} disabled={loading}>
          <RefreshCw size={16} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
          Refresh
        </button>
      </div>

      <div style={styles.grid}>
        <StatCard icon={<Key size={22} color="#6366f1" />} label="Total Keys" value={totalKeys} color="#6366f1" />
        <StatCard icon={<CheckCircle size={22} color="#4ade80" />} label="Aktif" value={activeKeys} color="#4ade80" />
        <StatCard icon={<XCircle size={22} color="#f87171" />} label="Expired/Nonaktif" value={totalKeys - activeKeys + expiredKeys} color="#f87171" />
        <StatCard icon={<Users size={22} color="#f59e0b" />} label="Sesi Aktif" value={activeSess} color="#f59e0b" />
      </div>

      {/* Recent licenses */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>License Terbaru</h2>
        <div style={styles.table}>
          <div style={styles.tableHeader}>
            <span style={{ flex: 2 }}>Key</span>
            <span style={{ flex: 1 }}>Status</span>
            <span style={{ flex: 1 }}>Uses</span>
            <span style={{ flex: 2 }}>Expire</span>
            <span style={{ flex: 1 }}>HWID</span>
          </div>
          {Object.entries(licenses).slice(-10).reverse().map(([key, lic]) => (
            <div key={key} style={styles.tableRow}>
              <span style={{ flex: 2, fontFamily: "monospace", fontSize: 12, color: "#a5b4fc" }}>
                {key}
              </span>
              <span style={{ flex: 1 }}>
                <span style={{
                  padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                  background: lic.active ? "#4ade8022" : "#f8717122",
                  color: lic.active ? "#4ade80" : "#f87171",
                }}>
                  {lic.active ? "AKTIF" : "NONAKTIF"}
                </span>
              </span>
              <span style={{ flex: 1, color: "#94a3b8", fontSize: 13 }}>
                {lic.uses_left === -1 ? "∞" : lic.uses_left}
              </span>
              <span style={{ flex: 2, color: "#94a3b8", fontSize: 12 }}>
                {lic.expire ? lic.expire.slice(0, 10) : "Unlimited"}
              </span>
              <span style={{ flex: 1, color: lic.hwid ? "#f59e0b" : "#64748b", fontSize: 11 }}>
                {lic.hwid ? "Bound" : "Free"}
              </span>
            </div>
          ))}
          {totalKeys === 0 && (
            <div style={{ padding: "24px", textAlign: "center", color: "#64748b" }}>
              Belum ada license
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: "32px 32px 32px 252px", minHeight: "100vh", fontFamily: "'Segoe UI', sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 },
  title: { color: "#fff", fontSize: 24, fontWeight: 700, margin: 0 },
  sub: { color: "#64748b", fontSize: 13, marginTop: 4 },
  refreshBtn: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "8px 16px", background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
    color: "#94a3b8", fontSize: 13, cursor: "pointer",
  },
  grid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 32 },
  card: {
    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "20px 24px", display: "flex", alignItems: "center", gap: 16,
  },
  cardIcon: { borderRadius: 10, padding: 10, display: "flex" },
  cardVal: { color: "#fff", fontSize: 28, fontWeight: 700, lineHeight: 1 },
  cardLabel: { color: "#64748b", fontSize: 13, marginTop: 4 },
  section: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12 },
  sectionTitle: { color: "#fff", fontSize: 16, fontWeight: 600, padding: "20px 24px 0", margin: 0 },
  table: { padding: "12px 0" },
  tableHeader: {
    display: "flex", padding: "8px 24px",
    color: "#64748b", fontSize: 12, fontWeight: 600,
    textTransform: "uppercase", letterSpacing: 0.5,
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  tableRow: {
    display: "flex", padding: "12px 24px", alignItems: "center",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
    transition: "background 0.15s",
  },
};
